// Turns a show into what one strand of lights should look like at a moment in
// time. Pure and deterministic: every house computes its own frames from the
// shared show and its clock, so the hub only sends small state changes, and
// every house (and every reconnect) renders exactly the same show. Even the
// "random" twinkle is hashed from the beat, section and house.

import {
  LIMITS,
  SECTION_EFFECTS,
  type BeatShowSpec,
  type EffectName,
  type Rgb,
  type Show,
  type ShowSpec,
} from "./protocol.ts";

// One strand's output. Standard Govee commands control a strand as one zone;
// real-time mode can also color its sections separately.
export interface Frame {
  on: boolean;
  // The whole strand's color; for a sectioned frame, its main color.
  rgb: Rgb;
  brightness: number;
  // Per-section colors along the strand (real-time mode only), at full
  // strength: brightness applies on top. Absent = the whole strand is rgb.
  segments?: Rgb[];
}

// How and where one strand renders. Set per strand, because it depends on
// the lights.
export interface RenderStyle {
  // For lights that fade between colors (standard Govee commands): flash to
  // full brightness on each beat, then drop low and change color while dim,
  // so the fade is hidden and every beat reads as a crisp flash.
  flash?: boolean;
  // Sections this strand can color separately (real-time mode). 0 or 1 =
  // whole strand only: per-section effects become their closest whole-strand
  // version.
  sections?: number;
  // This strand's place in the house (0-based) and the house's strand count,
  // so run, fill and chase carry on from one strand to the next.
  strand?: number;
  strands?: number;
  // The strand runs the other way (its section 0 is at the far end).
  reverse?: boolean;
  // 0-100: how dark the unlit parts get (between flashes, around a run or
  // comet). 100 = fully dark, 0 = softly lit. Default DEFAULT_CONTRAST.
  contrast?: number;
}

export const FLASH_ON_MS = 150;
const STROBE_ON_MS = 60;
// Between hard flashes: 1%, nearly dark, without the slow on/off commands.
const DARK_BRIGHTNESS = 1;
// Low point of the pulse swell; the gentler pulse that stands in for twinkle.
const PULSE_LOW = 0.15;
const SOFT_PULSE_LOW = 0.45;
const PULSE_RISE = 0.25;
// Per-section effects: unlit sections glow faintly so the house isn't dark.
// That's at the default contrast; see dimLevel.
const SECTION_DIM = 0.08;
export const DEFAULT_CONTRAST = 70;
const MIN_RATE = 0.001;
// The unlit level at contrast 0.
const SOFTEST_DIM = 0.5;
const RUN_TAIL = [0.45, 0.15];
const COMET_TAIL = [0.6, 0.42, 0.28, 0.18, 0.1];
// Marquee: several runs chasing evenly spaced along the strand.
const MARQUEE_DOTS = 4;
const TWINKLE_BASE = 0.12;
const TWINKLE_CHANCE = 35; // percent of sections that sparkle each cycle
// On fewer sections a "section" is a big piece of the strand, and a run or
// twinkle would read as the strand blinking, so they get the substitute.
export const MIN_SECTIONS = 4;

// How bright the unlit parts are (0 to 1) at a contrast (0-100): the default
// gives the original levels; 100 is dark, 0 softly lit.
export function dimLevel(contrast = DEFAULT_CONTRAST): number {
  const c = Math.min(100, Math.max(0, contrast));
  if (c >= DEFAULT_CONTRAST) return (SECTION_DIM * (100 - c)) / (100 - DEFAULT_CONTRAST);
  return SECTION_DIM + ((SOFTEST_DIM - SECTION_DIM) * (DEFAULT_CONTRAST - c)) / DEFAULT_CONTRAST;
}

export function isBeatShow(show: ShowSpec): show is BeatShowSpec {
  return show.kind === "beat" || show.kind === "chase" || show.kind === "effect";
}

// The original beat and chase modes are effects step and chase at rate 1.
export function effectOf(show: BeatShowSpec): { effect: EffectName; rate: number } {
  if (show.kind === "effect") return { effect: show.effect, rate: show.rate };
  return { effect: show.kind === "beat" ? "step" : "chase", rate: 1 };
}

interface Beat {
  bpm: number;
  palette: Rgb[];
  brightness: number;
  start: number;
}

// Whole-strand renderings. "soft" is the gentler pulse.
type Whole = "step" | "chase" | "pulse" | "soft" | "flash" | "strobe";

export function renderShow(show: Show, atMs: number, position: number, style: RenderStyle = {}): Frame {
  if (show.kind === "off") return { on: false, rgb: [0, 0, 0], brightness: 0 };
  if (show.kind === "solid") return { on: true, rgb: show.rgb, brightness: show.brightness };

  const { effect, rate } = effectOf(show);
  const beat: Beat = { bpm: show.bpm, palette: show.palette, brightness: show.brightness, start: show.startEpochMs ?? show.t };
  const strands = Math.max(1, Math.floor(style.strands ?? 1));
  const strand = Math.min(strands - 1, Math.max(0, Math.floor(style.strand ?? 0)));
  const sections = Math.min(LIMITS.maxSections, Math.floor(style.sections ?? 0));
  const flash = style.flash === true;
  const dim = dimLevel(style.contrast);
  // How far along the house this strand starts (0 to 1), for effects that
  // travel from strand to strand.
  const along = strand / strands;

  if (SECTION_EFFECTS.includes(effect) && sections >= MIN_SECTIONS) {
    const frame = renderSections(effect, rate, beat, atMs, position, sections, strand, strands, dim);
    if (style.reverse) frame.segments?.reverse();
    return frame;
  }

  // Whole strand: the effect itself, or the closest substitute.
  switch (effect) {
    case "step":
    case "pulse":
    case "flash":
    case "strobe":
      return renderWhole(effect, rate, beat, atMs, 0, 0, flash, dim);
    case "chase":
      // House N shows what house N-1 showed a beat earlier; within a house,
      // each strand a fraction of the beat after the one before.
      return renderWhole("chase", rate, beat, atMs, along, -position, flash, dim);
    case "run":
    case "comet":
    case "bounce":
    case "crossrun":
      // A hard flash as the run (or comet, bounce, crossing light) passes.
      return renderWhole("flash", rate, beat, atMs, along, 0, false, dim);
    case "marquee":
      // Quick strobe flashes as the chasers pass.
      return renderWhole("strobe", rate, beat, atMs, along, 0, false, dim);
    case "fill":
      return renderWhole("pulse", rate, beat, atMs, along, 0, false, dim);
    case "alternate":
      // Neighboring strands take turns with the colors.
      return renderWhole("step", rate, beat, atMs, 0, strand % 2, flash, dim);
    case "twinkle":
      return renderWhole("soft", rate, beat, atMs, 0, 0, false, dim);
    case "rainbow":
      // Slow color steps; fading lights blend them into a gentle flow.
      return renderWhole("step", rate / 4, beat, atMs, along, 0, false, dim);
  }
}

// `shift` delays the effect by that fraction of a cycle; `step` offsets the
// palette color.
function renderWhole(kind: Whole, rate: number, b: Beat, atMs: number, shift: number, step: number, flash: boolean, dim: number): Frame {
  // Speed 0 holds the effect still (a tiny speed keeps the math finite).
  const r = Math.max(rate, MIN_RATE);
  const cycleMs = 60000 / (b.bpm * r);
  const at = atMs - shift * cycleMs;
  const index = Math.floor((at - b.start) / cycleMs);
  const intoMs = at - b.start - index * cycleMs;
  const color = (k: number) => paletteColor(b.palette, k);
  const k = index + step;
  const lit = (rgb: Rgb, level = 1): Frame => ({
    on: true,
    rgb,
    brightness: Math.max(1, Math.round(b.brightness * level)),
  });

  switch (kind) {
    case "step":
    case "chase": {
      if (!flash) return lit(color(k));
      if (intoMs < Math.min(FLASH_ON_MS, cycleMs * 0.4)) return lit(color(k));
      // Dim, already turning to the next beat's color.
      return { on: true, rgb: color(k + 1), brightness: Math.max(1, Math.round(b.brightness * dim)) };
    }
    case "pulse":
    case "soft": {
      // Swell up quickly from the beat, then ease back down. The color
      // changes at the low point, where fading lights hide it best.
      const low = kind === "soft" ? Math.max(SOFT_PULSE_LOW, dim) : Math.min(0.9, (PULSE_LOW * dim) / SECTION_DIM);
      const phase = intoMs / cycleMs;
      const swell =
        phase < PULSE_RISE
          ? Math.sin((Math.PI / 2) * (phase / PULSE_RISE)) ** 2
          : Math.cos((Math.PI / 2) * ((phase - PULSE_RISE) / (1 - PULSE_RISE))) ** 2;
      return lit(color(k), low + (1 - low) * swell);
    }
    case "flash":
    case "strobe": {
      const onMs = kind === "flash" ? Math.min(FLASH_ON_MS, cycleMs * 0.4) : Math.min(STROBE_ON_MS, cycleMs * 0.25);
      if (intoMs < onMs) return lit(color(k));
      // Dark until the next flash, already in its color (hides fades).
      // Below the default contrast, the dark part lifts toward SOFTEST_DIM.
      const lift = dim > SECTION_DIM ? ((dim - SECTION_DIM) / (SOFTEST_DIM - SECTION_DIM)) * SOFTEST_DIM : 0;
      return { on: true, rgb: color(k + 1), brightness: Math.max(DARK_BRIGHTNESS, Math.round(b.brightness * lift)) };
    }
  }
}

function renderSections(
  effect: EffectName,
  rate: number,
  b: Beat,
  atMs: number,
  position: number,
  sections: number,
  strand: number,
  strands: number,
  dim: number,
): Frame {
  // Speed 0 holds the effect still (a tiny speed keeps the math finite).
  const r = Math.max(rate, MIN_RATE);
  const cycleMs = 60000 / (b.bpm * r);
  const cycles = (atMs - b.start) / cycleMs;
  const index = Math.floor(cycles);
  const phase = cycles - index;
  const n = b.palette.length;
  const color = (k: number) => paletteColor(b.palette, k);
  // Where this strand's part of a run or fill is (0 to 1): each strand of the
  // house takes its turn within the cycle.
  const local = phase * strands - strand;
  const segments: Rgb[] = [];
  const twinkleBase = Math.min(0.9, (TWINKLE_BASE * dim) / SECTION_DIM);

  switch (effect) {
    case "run": {
      const c = color(index);
      const head = local * (sections + RUN_TAIL.length);
      for (let i = 0; i < sections; i++) {
        const d = head - i;
        const level = d >= 0 && d < 1 ? 1 : d >= 1 ? (RUN_TAIL[Math.floor(d) - 1] ?? dim) : dim;
        segments.push(scale(c, Math.max(level, dim)));
      }
      break;
    }
    case "comet": {
      // Like run, but with a longer, smoother glowing tail.
      const c = color(index);
      const head = local * (sections + COMET_TAIL.length);
      for (let i = 0; i < sections; i++) {
        const d = head - i;
        const level = d >= 0 && d < 1 ? 1 : d >= 1 ? (COMET_TAIL[Math.floor(d) - 1] ?? dim) : dim;
        segments.push(scale(c, Math.max(level, dim)));
      }
      break;
    }
    case "marquee": {
      // Several runs, evenly spaced, chasing together.
      const c = color(index);
      const span = sections + RUN_TAIL.length;
      const head = local * span;
      for (let i = 0; i < sections; i++) {
        let level = dim;
        for (let k = 0; k < MARQUEE_DOTS; k++) {
          const d = mod(head - k * (span / MARQUEE_DOTS), span) - i;
          const l = d >= 0 && d < 1 ? 1 : d >= 1 ? (RUN_TAIL[Math.floor(d) - 1] ?? dim) : dim;
          if (l > level) level = l;
        }
        segments.push(scale(c, level));
      }
      break;
    }
    case "bounce": {
      // A lit section bounces between the two ends instead of wrapping.
      const c = color(index);
      const span = sections - 1;
      const active = local >= 0 && local < 1;
      const t = local < 0.5 ? local * 2 : (1 - local) * 2; // triangle wave: 0 -> 1 -> 0
      const headPos = t * span;
      for (let i = 0; i < sections; i++) {
        if (!active) {
          segments.push(scale(c, dim));
          continue;
        }
        const dist = Math.abs(i - headPos);
        const level = dist < 0.5 ? 1 : dist < 1.5 ? 0.45 : dist < 2.5 ? 0.18 : dim;
        segments.push(scale(c, Math.max(level, dim)));
      }
      break;
    }
    case "crossrun": {
      // Two lit sections run toward each other from opposite ends and cross.
      const c = color(index);
      const head = local * (sections + RUN_TAIL.length);
      for (let i = 0; i < sections; i++) {
        const dA = head - i;
        const lA = dA >= 0 && dA < 1 ? 1 : dA >= 1 ? (RUN_TAIL[Math.floor(dA) - 1] ?? dim) : dim;
        const dB = head - (sections - 1 - i);
        const lB = dB >= 0 && dB < 1 ? 1 : dB >= 1 ? (RUN_TAIL[Math.floor(dB) - 1] ?? dim) : dim;
        segments.push(scale(c, Math.max(lA, lB, dim)));
      }
      break;
    }
    case "fill": {
      // The next color wipes over the last one. With one color, it wipes in
      // and then out again, so the strand never blinks as a whole.
      const filled = Math.ceil(Math.min(1, Math.max(0, local)) * sections);
      let fresh = color(index);
      let old = color(index - 1);
      if (n === 1) {
        const dimColor = scale(color(0), dim);
        if (mod(index, 2) === 0) old = dimColor;
        else fresh = dimColor;
      }
      for (let i = 0; i < sections; i++) segments.push(i < filled ? fresh : old);
      break;
    }
    case "alternate": {
      for (let i = 0; i < sections; i++) {
        const odd = mod(i + strand, 2);
        if (n === 1) segments.push(mod(odd + index, 2) === 0 ? color(0) : scale(color(0), dim));
        else segments.push(color(index + odd));
      }
      break;
    }
    case "twinkle": {
      // Each cycle about a third of the sections sparkle, each starting at its
      // own moment, then fade back to a dim glow. Staggered, so the strand as
      // a whole never flashes.
      for (let i = 0; i < sections; i++) {
        const base = scale(color(i + strand), twinkleBase);
        const h = hash(index, strand * 64 + i, position);
        let seg = base;
        if (h % 100 < TWINKLE_CHANCE) {
          const start = (((h >>> 8) % 1000) / 1000) * 0.5;
          const t = (phase - start) / 0.5;
          if (t >= 0 && t < 1) {
            const level = (1 - t) ** 2;
            if (level > twinkleBase) seg = scale(color(h >>> 20), level);
          }
        }
        segments.push(seg);
      }
      break;
    }
    case "rainbow": {
      // The palette spread along the house, moving one color per cycle.
      for (let i = 0; i < sections; i++) {
        const u = (strand + (i + 0.5) / sections) / strands;
        const p = u * n - cycles;
        const k = Math.floor(p);
        segments.push(mix(color(k), color(k + 1), p - k));
      }
      break;
    }
    default:
      // Whole-strand effects never get here.
      for (let i = 0; i < sections; i++) segments.push(color(index));
  }
  return { on: true, rgb: color(index), brightness: b.brightness, segments };
}

function paletteColor(palette: Rgb[], k: number): Rgb {
  return palette[mod(k, palette.length)] ?? [0, 0, 0];
}

function mod(k: number, n: number): number {
  return ((k % n) + n) % n;
}

function scale(c: Rgb, level: number): Rgb {
  return [Math.round(c[0] * level), Math.round(c[1] * level), Math.round(c[2] * level)];
}

function mix(a: Rgb, b: Rgb, f: number): Rgb {
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ];
}

// Small integer hash (murmur3-style finalizer): same inputs, same result on
// every machine.
export function hash(a: number, b: number, c: number): number {
  let h = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77) ^ Math.imul(c | 0, 0xc2b2ae3d);
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

export function beatIndex(bpm: number, startEpochMs: number, atMs: number): number {
  return Math.floor((atMs - startEpochMs) / (60000 / bpm));
}

export function sameFrame(a: Frame | null, b: Frame | null): boolean {
  if (a === null || b === null) return a === b;
  if (!a.on && !b.on) return true;
  return (
    a.on === b.on &&
    a.brightness === b.brightness &&
    sameRgb(a.rgb, b.rgb) &&
    (a.segments === undefined
      ? b.segments === undefined
      : b.segments !== undefined && a.segments.length === b.segments.length && a.segments.every((c, i) => sameRgb(c, b.segments![i]!)))
  );
}

function sameRgb(a: Rgb, b: Rgb): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
